const DASH = '—';

const moneyFmt = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});
const intFmt = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });
const decFmt = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });

export function money(value: number | null | undefined, opts: { sign?: boolean } = {}): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  const body = `$${moneyFmt.format(Math.abs(value))}`;
  if (value < 0) return `−${body}`;
  return opts.sign && value > 0 ? `+${body}` : body;
}

export function num(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  return intFmt.format(value);
}

export function dec(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  return decFmt.format(value);
}

export function percent(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  return `${decFmt.format(value)}%`;
}

/** Durations given in seconds; switches to ms / minutes / hours where it reads better. */
export function seconds(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  const abs = Math.abs(value);
  if (abs < 1) return `${Math.round(value * 1000)} мс`;
  if (abs < 60) return `${decFmt.format(value)} с`;
  if (abs < 3600) {
    const m = Math.floor(abs / 60);
    const s = Math.round(abs % 60);
    return s ? `${m} м ${s} с` : `${m} м`;
  }
  const h = Math.floor(abs / 3600);
  const m = Math.round((abs % 3600) / 60);
  return m ? `${h} ч ${m} м` : `${h} ч`;
}

export function millis(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  if (Math.abs(value) < 1000) return `${Math.round(value)} мс`;
  return seconds(value / 1000);
}

/**
 * Postgres timestamps arrive either as ISO strings or as `2026-08-20 22:12:12.536+00`
 * (see catalog freshness / timeseries buckets), so normalise before parsing.
 */
export function parseDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

const dateTimeFmt = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});
const timeFmt = new Intl.DateTimeFormat('ru-RU', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});
const shortFmt = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

export function dateTime(value: string | number | Date | null | undefined): string {
  const d = parseDate(value);
  return d ? dateTimeFmt.format(d) : DASH;
}

export function time(value: string | number | Date | null | undefined): string {
  const d = parseDate(value);
  return d ? timeFmt.format(d) : DASH;
}

export function shortDateTime(value: string | number | Date | null | undefined): string {
  const d = parseDate(value);
  return d ? shortFmt.format(d) : DASH;
}

/** "3 м назад" style label used for queue ages and last-error timestamps. */
export function ago(value: string | number | Date | null | undefined): string {
  const d = parseDate(value);
  if (!d) return DASH;
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 0) return `через ${seconds(-diff)}`;
  return `${seconds(diff)} назад`;
}

export function boolLabel(value: boolean | null | undefined): string {
  if (value === null || value === undefined) return DASH;
  return value ? 'да' : 'нет';
}

export function text(value: string | null | undefined): string {
  return value && value.trim() ? value : DASH;
}

export function truncate(value: string | null | undefined, max = 60): string {
  if (!value) return DASH;
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

export const RANGES = [
  { label: '1 ч', hours: 1 },
  { label: '6 ч', hours: 6 },
  { label: '24 ч', hours: 24 },
  { label: '7 д', hours: 24 * 7 },
  { label: '30 д', hours: 24 * 30 },
] as const;

export const EMPTY = DASH;
